// Some profile records hold placeholder text instead of an empty value (e.g. a home phone stored as
// the literal string "NULL"). `value ?? ''` doesn't catch these, so they reach forms as real input,
// where they look wrong and can fail validation, which disables the submit button.
//
// Apply this where profile data enters the app, not to every API response: elsewhere 'NULL' and
// 'null' are meaningful values (see model/checkInCheckOutCenter and redux/medicalHistory).
const NULL_TEXT_VALUES = new Set(['null', 'undefined', 'nil', 'n/a']);

export const isNullText = (value: unknown): boolean =>
  typeof value === 'string' && NULL_TEXT_VALUES.has(value.trim().toLowerCase());

// Blanks placeholder strings and drops them from arrays. Only looks at top-level fields, and leaves
// every other value type untouched (e.g. the [year, month, day] dob tuples).
export const sanitizeNullText = <T>(record: T): T => {
  if (!record || typeof record !== 'object') {
    return record;
  }

  return Object.fromEntries(
    Object.entries(record).map(([key, value]) => {
      if (Array.isArray(value)) {
        return [key, value.filter((item) => !isNullText(item))];
      }

      return [key, isNullText(value) ? '' : value];
    })
  ) as T;
};
